import { FC, useEffect } from 'react';

import { Body } from 'components/text';
import Input from './components/input';

import { BrandIcon } from './components/brand-icon';

import { useFormContext } from 'react-hook-form';
import { SignUpStep3Schema } from 'blocks/sign-up/schemas';

import { ErrorMessage } from '@hookform/error-message';

import {
  normalizeCVC,
  normalizeExpiryDate,
  normalizeName,
  normalizeNumber,
} from './utils/normalize';
import { prettyCardNumber } from './utils/pretty';
import creditCardType from 'credit-card-type';

import styles from './CreditCardElement.module.scss';

const CreditCardElement: FC = () => {
  const {
    setValue,
    register,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext<SignUpStep3Schema>();

  const cardNumber = watch('creditCard.number');

  //Update brand on card number change
  useEffect(() => {
    const cardBrand = creditCardType(cardNumber)[0]?.type || 'visa';
    setValue('creditCard.brand', cardBrand);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNumber]);

  const handleNormalize =
    (e: React.ChangeEvent<HTMLInputElement>) =>
    (inputName: any, normalizeFn: (value: string) => string) => {
      const { value } = e.target;
      const normalizedValue = normalizeFn(value);
      setValue(inputName, normalizedValue);
      e.target.value = normalizedValue;
    };

  const handlePretty =
    (e: React.ChangeEvent<HTMLInputElement>) =>
    (prettyFn: (value: string) => string) => {
      const { value } = e.target;
      const prettyValue = prettyFn(value);
      e.target.value = prettyValue;
    };

  return (
    <div className={styles.container}>
      <Input
        {...register('creditCard.number', {
          onChange: e => {
            clearErrors('creditCard.number');
            handleNormalize(e)('creditCard.number', normalizeNumber);
            handlePretty(e)(prettyCardNumber);
          },
        })}
        label="Card number"
        placeholder="0000 0000 0000 0000"
        rounded={['tl', 'tr']}
        borders={['t', 'r', 'l']}
        hasError={!!errors?.creditCard?.number}
      >
        <BrandIcon />
      </Input>

      <div className={styles.expiryCvvContainer}>
        <Input
          {...register('creditCard.expiry', {
            onChange: e => {
              clearErrors('creditCard.expiry');
              handleNormalize(e)('creditCard.expiry', normalizeExpiryDate);
            },
          })}
          label="Expiry date"
          placeholder="MM/YY"
          rounded={['bl']}
          className={styles.expiryInput}
          hasError={!!errors?.creditCard?.expiry?.message}
        />

        <Input
          {...register('creditCard.cvc', {
            onChange: e => {
              clearErrors('creditCard.cvc');
              handleNormalize(e)('creditCard.cvc', normalizeCVC);
            },
          })}
          label="CVV"
          placeholder="123"
          rounded={['br']}
          borders={['t', 'r', 'b']}
          hasError={!!errors?.creditCard?.cvc}
        />
      </div>

      {[
        'creditCard',
        'creditCard.number',
        'creditCard.expiry',
        'creditCard.cvc',
      ].map(fieldName => (
        <ErrorMessage
          key={fieldName}
          render={({ message }) => (
            <Body mt={5} color="forms-red">
              {message}
            </Body>
          )}
          name={fieldName}
        />
      ))}

      <Input
        {...register('creditCard.name', {
          onChange: e => {
            clearErrors('creditCard.name');
            handleNormalize(e)('creditCard.name', normalizeName);
          },
        })}
        placeholder="John Snow"
        className={styles.name}
        label="Cardholder name"
        hasError={!!errors?.creditCard?.name}
      />
      <ErrorMessage
        render={({ message }) => (
          <Body mt={5} color="forms-red">
            {message}
          </Body>
        )}
        name="creditCard.name"
      />
    </div>
  );
};

export default CreditCardElement;
